import { lazy, Suspense, type ReactNode } from "react";
import { Routes, Route, Outlet, useNavigate, useParams, Navigate } from "react-router-dom";
import { ErrorPage } from "@safers/ui/templates";
import {
  ProtectedRouter,
  logout,
  useAuthStore,
  selectUser,
  canAccessSite,
  canAccessFeature,
  hasFeatureAccess,
  type SiteFeature,
} from "@safers/auth";
import { Spinner } from "@safers/ui/atoms";

import { LoginPage } from "@/pages";
import { MobileShell } from "@/components/mobile/MobileShell";

const ControlLayout = lazy(() =>
  import("@/routes/ControlLayout").then((m) => ({ default: m.ControlLayout }))
);
// 신규 전국 집계 대시보드(#184). 기존 "/" 와 비교하며 다듬는 중이라 별도 경로로 둔다.
// 3D 현장뷰(#182) — 동선에서 빠졌다. 코드는 pages/site-3d 에 보존돼 있고 라우트는 없다.
const HealthPage = lazy(() => import("@/pages/health").then((m) => ({ default: m.HealthPage })));
const SafetyReportPage = lazy(() =>
  import("@/pages/safety-report").then((m) => ({ default: m.SafetyReportPage }))
);
const CCTVAIPage = lazy(() => import("@/pages/cctv-ai").then((m) => ({ default: m.CCTVAIPage })));
const CCTVPlaybackPage = lazy(() =>
  import("@/pages/cctv-playback").then((m) => ({ default: m.CCTVPlaybackPage }))
);
const POC3DPage = lazy(() => import("@/pages/poc-3d").then((m) => ({ default: m.POC3DPage })));

function LazyFallback() {
  return (
    <div className="flex h-screen w-screen items-center justify-center">
      <Spinner size="lg" />
    </div>
  );
}

function NotFoundPage() {
  const navigate = useNavigate();
  return (
    <ErrorPage variant="404" primaryAction={{ label: "홈으로", onClick: () => navigate("/") }} />
  );
}

function ForbiddenPage() {
  const navigate = useNavigate();
  // 403 도달자는 이미 로그인된 상태이므로 "다시 로그인" 대신 "로그아웃"이 명확.
  // 다른 계정으로 전환하려면 로그아웃 후 /login으로 이동시킨다.
  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      // 서버 실패해도 클라이언트 세션은 정리
    } finally {
      useAuthStore.getState().reset();
      navigate("/login", { replace: true });
    }
  };
  return (
    <ErrorPage
      variant="403"
      primaryAction={{ label: "홈으로", onClick: () => navigate("/") }}
      secondaryAction={{ label: "로그아웃", onClick: handleLogout }}
    />
  );
}

function SiteGate({
  param,
  feature,
  children,
}: {
  param: "id" | "siteId";
  /** 지정 시 현장 접근에 더해 이 기능(subResource) 권한까지 요구 */
  feature?: SiteFeature;
  children: ReactNode;
}) {
  const params = useParams();
  const user = useAuthStore(selectUser);
  const siteId = params[param];
  // 정상 UI 로는 도달하지 않는 경로(직접 URL·공유 딥링크·회수된 북마크)이므로
  // 조용히 홈으로 튕기기보다 403 으로 "권한 없음"을 명시한다.
  if (siteId != null && !canAccessSite(user, siteId)) {
    return <Navigate to="/403" replace />;
  }
  if (siteId != null && feature && !canAccessFeature(user, siteId, feature)) {
    return <Navigate to="/403" replace />;
  }
  return <>{children}</>;
}

function FeatureGate({ feature, children }: { feature: SiteFeature; children: ReactNode }) {
  const user = useAuthStore(selectUser);
  if (!hasFeatureAccess(user, feature)) {
    return <Navigate to="/403" replace />;
  }
  return <>{children}</>;
}

function AuthLayout() {
  return (
    <ProtectedRouter
      fallback={
        <div className="flex h-screen w-screen items-center justify-center">
          <Spinner size="lg" />
        </div>
      }
    >
      <Outlet />
    </ProtectedRouter>
  );
}

export function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/poc-3d"
        element={
          <Suspense fallback={<LazyFallback />}>
            <POC3DPage />
          </Suspense>
        }
      />
      {/* 근로자용 현장 페이지는 별도 앱(safers-worker, /worker 배포)으로 분리됨 */}
      {/* USER가 admin URL 직접 진입 시 admin AuthorityGate가 이 경로로 redirect */}
      <Route path="/403" element={<ForbiddenPage />} />
      <Route path="/404" element={<NotFoundPage />} />

      {/* Protected — 인증 필요 */}
      <Route element={<AuthLayout />}>
        <Route element={<MobileShell />}>
          {/*
            전국(`/`)과 현장(`/site/:id`)은 **한 화면의 두 스코프**다. 레이아웃 라우트로
            묶어야 스코프를 오갈 때 컴포넌트가 다시 붙지 않는다 — 지도 인스턴스가
            유지되어야 한 번의 비행으로 이어지고, 헤더도 깜빡이지 않는다.
          */}
          <Route
            element={
              <Suspense fallback={<LazyFallback />}>
                <ControlLayout />
              </Suspense>
            }
          >
            <Route index element={null} />
            <Route path="site/:id" element={null} />
          </Route>
          {/* 개발 중 쓰던 주소 — 북마크·공유 링크가 남아 있어 홈으로 넘긴다. */}
          <Route path="dashboard" element={<Navigate to="/" replace />} />
          <Route
            path="safety-report/:siteId"
            element={
              <SiteGate param="siteId" feature="HEALTH">
                <Suspense fallback={<LazyFallback />}>
                  <SafetyReportPage />
                </Suspense>
              </SiteGate>
            }
          />
          <Route
            path="cctv-ai/:siteId"
            element={
              <SiteGate param="siteId" feature="CCTV">
                <Suspense fallback={<LazyFallback />}>
                  <CCTVAIPage />
                </Suspense>
              </SiteGate>
            }
          />
          <Route
            path="cctv-playback/:siteId"
            element={
              <SiteGate param="siteId" feature="CCTV">
                <Suspense fallback={<LazyFallback />}>
                  <CCTVPlaybackPage />
                </Suspense>
              </SiteGate>
            }
          />
          <Route
            path="health"
            element={
              <FeatureGate feature="HEALTH">
                <Suspense fallback={<LazyFallback />}>
                  <HealthPage />
                </Suspense>
              </FeatureGate>
            }
          />
        </Route>
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
